import { FC } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import {
  Widget,
  WidgetAvatarsListItem,
  WidgetEmptyState,
} from '@factorialco/f0-react/dist/experimental'
import { Maximize as MaximizeIcon } from '@factorialco/f0-react/icons/app'
import {
  AbsencesHomeWidgetDocument,
  AbsencesHomeWidgetQuery,
  TimeInsightsEntitiesClockInStatusStatusEnum,
} from 'generated/resources/graphql'
import groupBy from 'lodash/groupBy'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { useActivities } from 'modules/core/lib/activities'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'
import { buildWidget } from 'modules/core/lib/widgets'

import { NEW_HOME_WIDGETS, NEW_HOME_WIDGET_ACTION_TYPES } from 'modules/dashboard/lib/events'

import './index.styles.css'
type UseLinkAndTitleParams = {
  firstTabStatus?: TimeInsightsEntitiesClockInStatusStatusEnum
}

const useLinkAndTitle = ({ firstTabStatus }: UseLinkAndTitleParams = {}) => {
  const title = i18n.t('new_dashboard.absences.title')

  const { openActivity } = useActivities()

  const handleClick = () => {
    if (!firstTabStatus) return

    track(trackingEvents.dashboard.clickOnHomeWidgetAction, {
      widget: NEW_HOME_WIDGETS.ABSENCES,
      actionType: NEW_HOME_WIDGET_ACTION_TYPES.UPPER_RIGHT_BUTTON,
    })
    openActivity('core.dashboardTeamStatus', {
      status: firstTabStatus,
    })
  }

  const link = firstTabStatus
    ? {
        title,
        icon: MaximizeIcon,
        onClick: handleClick,
      }
    : undefined

  return {
    title,
    link,
  }
}

const normalizeEmployeeToAvatar = (
  employee: NonNullable<
    AbsencesHomeWidgetQuery['timeInsights']['clockInStatusesConnection']['nodes'][number]['employee']
  >
) => ({
  firstName: employee.firstName,
  lastName: employee.lastName,
  src: employee.avatar ? buildUrl(employee.avatar.url) : undefined,
  type: 'person' as const,
})

const prepareData = (data: AbsencesHomeWidgetQuery) => {
  const statuses = data.timeInsights.clockInStatusesConnection.nodes || []
  const groups = groupBy(statuses, 'status')

  const outOfOfficeAvatars =
    groups.should_not_be_working
      ?.map((status) => (status.employee ? normalizeEmployeeToAvatar(status.employee) : null))
      .filter(Boolean) ?? []
  const missingClockInAvatars =
    groups.missing_clock_in
      ?.map((status) => (status.employee ? normalizeEmployeeToAvatar(status.employee) : null))
      .filter(Boolean) ?? []
  const clockedInAvatars =
    groups.clocked_in
      ?.map((status) => (status.employee ? normalizeEmployeeToAvatar(status.employee) : null))
      .filter(Boolean) ?? []
  const inABreakAvatars =
    groups.in_a_break
      ?.map((status) => (status.employee ? normalizeEmployeeToAvatar(status.employee) : null))
      .filter(Boolean) ?? []

  return {
    avatars: {
      missingClockIn: missingClockInAvatars,
      clockedIn: clockedInAvatars,
      inABreak: inABreakAvatars,
      outOfOffice: outOfOfficeAvatars,
    },
    counters: {
      missingClockIn: groups.missing_clock_in?.length ?? 0,
      clockedIn: groups.clocked_in?.length ?? 0,
      inABreak: groups.in_a_break?.length ?? 0,
      outOfOffice: groups.should_not_be_working?.length ?? 0,
    },
  }
}

type PreparedData = ReturnType<typeof prepareData>

const STATUSES = ['missing_clock_in', 'clocked_in', 'in_a_break', 'should_not_be_working'] as const

const AbsencesWidgetComponent: FC<{ data: PreparedData; employeeId: string }> = ({
  data: { avatars, counters },
}) => {
  const firstTabStatus =
    STATUSES[
      Object.keys(counters).findIndex(
        // eslint-disable-next-line no-type-assertion/no-type-assertion
        (status) => counters[status as unknown as keyof typeof counters] > 0
      )
    ]

  const { title, link } = useLinkAndTitle({
    firstTabStatus,
  })

  const { openActivity } = useActivities()

  const handleClickItem = (status: TimeInsightsEntitiesClockInStatusStatusEnum) => {
    track(trackingEvents.dashboard.clickOnHomeWidgetItem, {
      widget: NEW_HOME_WIDGETS.ABSENCES,
      type: status,
    })
    openActivity('core.dashboardTeamStatus', {
      status,
    })
  }

  const handleClickOutOfOffice = () => {
    handleClickItem('should_not_be_working')
  }

  const handleClickMissingClockIn = () => {
    handleClickItem('missing_clock_in')
  }

  const handleClickClockedIn = () => {
    handleClickItem('clocked_in')
  }

  const handleClickInABreak = () => {
    handleClickItem('in_a_break')
  }

  return (
    <Widget
      header={{
        title,
        link,
      }}
    >
      <Box className={'Absences_container'}>
        {counters.missingClockIn > 0 && (
          <WidgetAvatarsListItem
            id="missing-clock-in"
            title={i18n.t('dashboard.who_is_in.missing_clock_in')}
            subtitle={`${counters.missingClockIn} ${i18n.tp('common.person', {
              count: counters.missingClockIn,
            })}`}
            alert="critical"
            avatars={avatars.missingClockIn}
            onClick={handleClickMissingClockIn}
          />
        )}
        {counters.clockedIn > 0 && (
          <WidgetAvatarsListItem
            id="clocked-in"
            title={i18n.t('dashboard.who_is_in.clocked_in')}
            subtitle={`${counters.clockedIn} ${i18n.tp('common.person', {
              count: counters.clockedIn,
            })}`}
            alert="positive"
            avatars={avatars.clockedIn}
            onClick={handleClickClockedIn}
          />
        )}
        {counters.inABreak > 0 && (
          <WidgetAvatarsListItem
            id="in-a-break"
            title={i18n.t('dashboard.who_is_in.in_a_break')}
            subtitle={`${counters.inABreak} ${i18n.tp('common.person', {
              count: counters.inABreak,
            })}`}
            alert="warning"
            avatars={avatars.inABreak}
            onClick={handleClickInABreak}
          />
        )}
        {counters.outOfOffice > 0 && (
          <WidgetAvatarsListItem
            id="away"
            title={i18n.t('employees.away')}
            subtitle={`${counters.outOfOffice} ${i18n.tp('common.person', {
              count: counters.outOfOffice,
            })}`}
            alert="info"
            avatars={avatars.outOfOffice}
            onClick={handleClickOutOfOffice}
          />
        )}
      </Box>
    </Widget>
  )
}

const EmptyComponent = () => {
  const { link, title } = useLinkAndTitle()

  return (
    <Widget header={{ title, link }}>
      <WidgetEmptyState
        title={i18n.t('new_dashboard.absences.empty.title')}
        description={i18n.t('new_dashboard.absences.empty.description')}
        emoji="🏝️"
      />
    </Widget>
  )
}

export const buildAbsencesWidget = () =>
  buildWidget({
    id: 'absences',
    document: AbsencesHomeWidgetDocument,
    emptyComponent: () => <EmptyComponent />,
    isEmpty: ({ counters }) => Object.values(counters).every((counter) => counter === 0),
    prepareData,
    component: AbsencesWidgetComponent,
  })
